import { useState } from "react";
import { avisarError } from "../../shared/errores";
import { eliminarTarea, guardarTarea } from "./proyectosService";
import { SelectorPersona, Ventana } from "./componentes";
import Comentarios from "./Comentarios";
import Documentos from "./Documentos";
import { botonFantasma, botonPeligro, botonPrimario, campo, etiqueta } from "./estilos";
import { ESTADOS_TAREA, PRIORIDADES, esResponsable, fechaHora, gestiona } from "./reglas";
import { confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";

const VACIO = {
  titulo: "", descripcion: "", responsable_id: "", fecha_inicio: "", fecha_limite: "",
  estado: "pendiente", prioridad: "media",
};

const llenar = (tarea, inicial) => {
  const form = { ...VACIO, ...inicial };
  Object.keys(VACIO).forEach((c) => { if (tarea?.[c] != null) form[c] = tarea[c]; });
  return form;
};

// Crear, editar o consultar una tarea. Quien gestiona el proyecto edita todo; el
// responsable solo cambia el estado; los demás la ven y comentan. Al crearla queda
// abierta para adjuntar documentos y comentar enseguida, como en el portal.
function FormTarea({ tarea: tareaInicial, inicial, proyecto, personas, usuario, onCambio, onCerrar }) {
  const [tarea, setTarea] = useState(tareaInicial);
  const [form, setForm] = useState(() => llenar(tareaInicial, inicial));
  const [guardando, setGuardando] = useState(false);
  const editaTodo = gestiona(usuario, proyecto);
  const editaEstado = editaTodo || esResponsable(usuario, tarea);
  const soloLectura = Boolean(tarea) && !editaTodo;
  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });
  // Lo propio se reconoce por el miembro (cada cuenta es de un miembro).
  const propio = (id) => usuario?.miembro_id != null && id === usuario.miembro_id;

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim()) return avisarError(new Error("La tarea necesita un título."));
    if (form.fecha_inicio && form.fecha_limite && form.fecha_limite < form.fecha_inicio) {
      return avisarError(new Error("La fecha límite es anterior a la de inicio."));
    }
    setGuardando(true);
    try {
      const datos = editaTodo ? { ...form, proyecto_id: proyecto.id } : { estado: form.estado };
      if (tarea) {
        await guardarTarea(tarea.id, datos);
        onCambio();
        onCerrar();
      } else {
        const creada = await guardarTarea(null, datos);
        setTarea(creada);
        setForm(llenar(creada));
        onCambio();
      }
    } catch (error) {
      avisarError(error);
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    if (!(await confirmar(`¿Eliminar la tarea "${tarea.titulo}"? También se borran sus comentarios y documentos. Esta acción no se puede deshacer.`, { aceptar: "Eliminar" }))) return;
    try {
      await eliminarTarea(tarea);
      onCambio();
      onCerrar();
    } catch (error) {
      avisarError(error);
    }
  };

  return (
    <Ventana titulo={tarea ? (editaTodo ? "Editar tarea" : "Tarea") : "Nueva tarea"} subtitulo={proyecto.nombre} onCerrar={onCerrar} ancho="max-w-3xl">
      <form onSubmit={guardar}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={etiqueta} htmlFor="tarea-titulo">Título *</label>
            <input id="tarea-titulo" className={campo} value={form.titulo} onChange={cambiar("titulo")} disabled={soloLectura} placeholder="¿Qué hay que hacer?" autoFocus={!tarea} />
          </div>
          <div className="sm:col-span-2">
            <label className={etiqueta} htmlFor="tarea-descripcion">Descripción</label>
            <textarea id="tarea-descripcion" className={`${campo} min-h-[80px]`} value={form.descripcion} onChange={cambiar("descripcion")} disabled={soloLectura} placeholder="Detalles, enlaces, notas…" />
          </div>
          <div>
            <label className={etiqueta} htmlFor="tarea-responsable">Responsable</label>
            <SelectorPersona
              id="tarea-responsable"
              personas={personas}
              valor={form.responsable_id}
              onCambiar={(id) => setForm({ ...form, responsable_id: id })}
              vacio="Sin asignar"
              deshabilitado={soloLectura}
            />
          </div>
          <div>
            <label className={etiqueta} htmlFor="tarea-estado">Estado</label>
            <Selector id="tarea-estado" className={campo} value={form.estado} onChange={cambiar("estado")} disabled={Boolean(tarea) && !editaEstado}>
              {ESTADOS_TAREA.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
            </Selector>
          </div>
          <div>
            <label className={etiqueta} htmlFor="tarea-prioridad">Prioridad</label>
            <Selector id="tarea-prioridad" className={campo} value={form.prioridad} onChange={cambiar("prioridad")} disabled={soloLectura}>
              {PRIORIDADES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </Selector>
          </div>
          <div className="hidden sm:block" />
          <div>
            <label className={etiqueta} htmlFor="tarea-inicio">Inicio</label>
            <input id="tarea-inicio" type="date" className={campo} value={form.fecha_inicio} onChange={cambiar("fecha_inicio")} disabled={soloLectura} />
          </div>
          <div>
            <label className={etiqueta} htmlFor="tarea-limite">Fecha límite</label>
            <input id="tarea-limite" type="date" className={campo} value={form.fecha_limite} min={form.fecha_inicio || undefined} onChange={cambiar("fecha_limite")} disabled={soloLectura} />
          </div>
        </div>

        {tarea && (
          <p className="mt-4 text-[11px] text-gray-500">
            {[
              tarea.creado_por && `Creada por ${tarea.creado_por}`,
              tarea.creado_en && `Creada ${fechaHora(tarea.creado_en)}`,
              tarea.completada_en && `Completada ${fechaHora(tarea.completada_en)}`,
            ].filter(Boolean).join(" · ")}
          </p>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {tarea && editaTodo && <button type="button" onClick={eliminar} className={botonPeligro}>Eliminar</button>}
          <div className="flex-1" />
          <button type="button" onClick={onCerrar} className={botonFantasma}>{!tarea || editaEstado ? "Cancelar" : "Cerrar"}</button>
          {(!tarea || editaEstado) && (
            <button type="submit" disabled={guardando} className={botonPrimario}>{guardando ? "Guardando…" : "Guardar"}</button>
          )}
        </div>
      </form>

      {tarea && (
        <>
          <div className="mt-8 border-t border-white/10 pt-6">
            <Documentos
              compacto
              destino={{ tarea_id: tarea.id }}
              puedeSubir={editaEstado}
              puedeBorrar={(a) => editaTodo || propio(a.subido_por_id)}
              onCambio={onCambio}
            />
          </div>
          <div className="mt-8 border-t border-white/10 pt-6">
            <Comentarios tareaId={tarea.id} puedeBorrar={(c) => editaTodo || propio(c.autor_id)} onCambio={onCambio} />
          </div>
        </>
      )}
    </Ventana>
  );
}

export default FormTarea;
